import { Fragment, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { UseQueryResult } from '@tanstack/react-query'
import { ChevronDown, ChevronRight, Download } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  GranularityField,
  GroupingField,
  PeriodField,
  type Granularity,
  type Grouping,
  type Period,
} from '@/components/IncomeReportFilters'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { ApiError, getJSON } from '@/lib/api'
import { AccountingSyncChip } from '@/components/AccountingSyncChip'
import {
  IncomeReportInvoiceRow,
  type IncomeReportInvoice,
} from '@/components/IncomeReportInvoiceRow'
import { IncomeDrillDownAmountsRow } from '@/components/IncomeDrillDownAmountsRow'
import type { IncomeInvoiceDrillDown } from '@/lib/incomeInvoiceDrillDown'
import { ViewOnlyBadge } from '@/components/templates/app-shell'
import { OwnerFilter } from '@/components/OwnerFilter'

export interface IncomeAmounts {
  invoice_count: number
  gross: string
  gross_cents: number
  discount: string
  discount_cents: number
  tax: string
  tax_cents: number
  net: string
  net_cents: number
}

export interface IncomeRow extends IncomeAmounts {
  key: string
  month: string | null
  label: string
  invoices: IncomeReportInvoice[]
}

interface IncomeReportResponse {
  income_report: {
    period: Period
    grouping: Grouping
    granularity: Granularity | null
    group_heading: string
    starts_on: string
    ends_on: string
    invoice_count: number
    rows: IncomeRow[]
    total: IncomeAmounts
  }
}

interface RangeSelection {
  period: Period
  startsOn?: string
  endsOn?: string
  grouping?: Grouping
  granularity?: Granularity
  owner?: string
}

export function IncomeReportPage({
  onOpenInvoice,
  onDrillDown,
}: {
  onOpenInvoice: (invoiceId: number) => void
  onDrillDown: (drillDown: IncomeInvoiceDrillDown) => void
}) {
  const [form, setForm] = useState<RangeSelection>({
    period: 'this_month',
    grouping: 'month',
    granularity: 'month',
  })
  const [selection, setSelection] = useState<RangeSelection>({ period: 'this_month' })
  const [owner, setOwner] = useState<string | undefined>()
  const path = incomeReportPath(selection)
  const reportQuery = useQuery({
    queryKey: [
      'income-report',
      selection.period,
      selection.startsOn,
      selection.endsOn,
      selection.grouping,
      selection.granularity,
      selection.owner,
    ],
    queryFn: () => getJSON<IncomeReportResponse>(path),
    retry: false,
  })
  const report = reportQuery.data?.income_report
  const startsOn = form.startsOn ?? report?.starts_on ?? ''
  const endsOn = form.endsOn ?? report?.ends_on ?? ''

  return (
    <div className="mx-auto w-full max-w-[1200px] px-6 py-6">
      <header>
        <div className="flex items-center gap-3">
          <h1 className="text-heading-sm font-semibold">Income report</h1>
          <ViewOnlyBadge />
        </div>
        <p className="text-caption text-muted-foreground">
          Invoiced amounts by issue date · Association clock
        </p>
      </header>
      <form
        className="mt-4 flex flex-wrap items-end justify-between gap-3"
        onSubmit={(event) => {
          event.preventDefault()
          setSelection({ ...form, startsOn, endsOn, owner })
        }}
      >
        <div className="flex flex-wrap items-end gap-3">
          <PeriodField
            value={form.period}
            onChange={(period) =>
              setForm({
                ...form,
                period,
                startsOn: period === 'custom' ? startsOn : undefined,
                endsOn: period === 'custom' ? endsOn : undefined,
              })
            }
          />
          <GroupingField
            value={form.grouping ?? 'month'}
            onChange={(grouping) => setForm({ ...form, grouping })}
          />
          {(form.grouping ?? 'month') === 'month' && (
            <GranularityField
              value={form.granularity ?? 'month'}
              onChange={(granularity) => setForm({ ...form, granularity })}
            />
          )}
          <DateField
            label="From"
            value={startsOn}
            disabled={form.period !== 'custom'}
            onChange={(value) => setForm({ ...form, startsOn: value })}
          />
          <DateField
            label="To"
            value={endsOn}
            disabled={form.period !== 'custom'}
            onChange={(value) => setForm({ ...form, endsOn: value })}
          />
          <Button size="sm" type="submit">
            Apply
          </Button>
          <OwnerFilter value={owner} onChange={setOwner} />
        </div>
        <Button size="sm" variant="outline" asChild>
          <a href={incomeReportPath(selection, true)} download>
            <Download className="mr-2 h-4 w-4" />
            Export CSV
          </a>
        </Button>
      </form>
      <div className="mt-4 mb-2 flex items-center justify-between">
        <p className="text-caption text-muted-foreground">
          {report
            ? `${report.invoice_count} ${report.invoice_count === 1 ? 'invoice' : 'invoices'}`
            : '— invoices'}
        </p>
        <AccountingSyncChip />
      </div>
      <ReportBody query={reportQuery} onOpenInvoice={onOpenInvoice} onDrillDown={onDrillDown} />
    </div>
  )
}

function DateField({
  label,
  value,
  disabled,
  onChange,
}: {
  label: string
  value: string
  disabled: boolean
  onChange: (value: string) => void
}) {
  const id = `income-report-${label.toLowerCase()}`
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        className="h-9 w-40"
        type="date"
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  )
}

function ReportBody({
  query,
  onOpenInvoice,
  onDrillDown,
}: {
  query: UseQueryResult<IncomeReportResponse, Error>
  onOpenInvoice: (invoiceId: number) => void
  onDrillDown: (drillDown: IncomeInvoiceDrillDown) => void
}) {
  if (query.isLoading) return <EmptyState title="Loading income report" className="min-h-52" />
  if (query.isError) {
    return <EmptyState title={incomeReportError(query.error)} className="min-h-52" />
  }

  const report = query.data?.income_report
  if (!report || report.rows.length === 0) {
    return (
      <EmptyState
        className="mt-2 min-h-52"
        variant="quiet"
        title="No invoices in this range."
        description="Adjust the period or issue an invoice."
      />
    )
  }

  return (
    <IncomeTable
      rows={report.rows}
      total={report.total}
      groupHeading={report.group_heading}
      startsOn={report.starts_on}
      endsOn={report.ends_on}
      grouping={report.grouping}
      granularity={report.granularity}
      onOpenInvoice={onOpenInvoice}
      onDrillDown={onDrillDown}
    />
  )
}

function IncomeTable({
  rows,
  total,
  groupHeading,
  startsOn,
  endsOn,
  grouping,
  granularity,
  onOpenInvoice,
  onDrillDown,
}: {
  rows: IncomeRow[]
  total: IncomeAmounts
  groupHeading: string
  startsOn: string
  endsOn: string
  grouping: Grouping
  granularity: Granularity | null
  onOpenInvoice: (invoiceId: number) => void
  onDrillDown: (drillDown: IncomeInvoiceDrillDown) => void
}) {
  return (
    <div className="overflow-hidden rounded-md border border-border bg-card">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[52%]">{groupHeading}</TableHead>
            {['Gross', 'Discount', 'Tax', 'Net'].map((heading) => (
              <TableHead key={heading} className="text-right">
                {heading}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <IncomeGroup
              key={row.key}
              row={row}
              onOpenInvoice={onOpenInvoice}
              onDrillDown={onDrillDown}
              scope={{ startsOn, endsOn, grouping, granularity }}
            />
          ))}
          <IncomeDrillDownAmountsRow
            label="Total"
            amounts={total}
            className="pl-3"
            onDrillDown={onDrillDown}
            scope={{ startsOn, endsOn, grouping, granularity, groupKey: 'total' }}
            total
          />
        </TableBody>
      </Table>
    </div>
  )
}

function IncomeGroup({
  row,
  onOpenInvoice,
  onDrillDown,
  scope,
}: {
  row: IncomeRow
  onOpenInvoice: (invoiceId: number) => void
  onDrillDown: (drillDown: IncomeInvoiceDrillDown) => void
  scope: Omit<
    Pick<IncomeInvoiceDrillDown, 'startsOn' | 'endsOn' | 'grouping' | 'granularity' | 'groupKey'>,
    'groupKey'
  >
}) {
  const [expanded, setExpanded] = useState(true)
  const Icon = expanded ? ChevronDown : ChevronRight

  return (
    <Fragment>
      <TableRow className="bg-muted hover:bg-muted">
        <TableCell className="pl-3 font-medium">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-auto border-0 bg-transparent p-0 font-medium hover:bg-transparent"
            aria-label={`${expanded ? 'Collapse' : 'Expand'} ${row.label}`}
            onClick={() => setExpanded((current) => !current)}
          >
            <Icon className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
            {row.label} ({row.invoice_count})
          </Button>
        </TableCell>
        <TableCell />
        <TableCell />
        <TableCell />
        <TableCell />
      </TableRow>
      {expanded &&
        row.invoices.map((invoice) => (
          <IncomeReportInvoiceRow
            key={invoice.id}
            invoice={invoice}
            onOpenInvoice={onOpenInvoice}
          />
        ))}
      <IncomeDrillDownAmountsRow
        label={row.label}
        displayLabel={`Total for ${row.label}`}
        amounts={row}
        className="pl-8"
        onDrillDown={onDrillDown}
        scope={{ ...scope, groupKey: row.key }}
      />
    </Fragment>
  )
}

function incomeReportPath(selection: RangeSelection, exportCsv = false) {
  const params = new URLSearchParams({ period: selection.period })
  if (selection.period === 'custom' && selection.startsOn && selection.endsOn) {
    params.set('starts_on', selection.startsOn)
    params.set('ends_on', selection.endsOn)
  }
  if (selection.grouping && selection.grouping !== 'month') {
    params.set('grouping', selection.grouping)
  }
  if (
    selection.grouping === 'month' &&
    selection.granularity &&
    selection.granularity !== 'month'
  ) {
    params.set('grouping', 'month')
    params.set('granularity', selection.granularity)
  }
  if (selection.owner) params.set('owner', selection.owner)
  const endpoint = exportCsv ? '/api/v1/income_report/export' : '/api/v1/income_report'
  return `${endpoint}?${params}`
}

function incomeReportError(error: Error) {
  if (!(error instanceof ApiError) || typeof error.body !== 'object' || error.body === null) {
    return 'Income report unavailable'
  }
  const errors = (error.body as { errors?: Record<string, string[]> }).errors
  return errors
    ? (Object.values(errors).flat()[0] ?? 'Income report unavailable')
    : 'Income report unavailable'
}
